/* Ol Map */
import { Map, Feature, View } from 'ol';
import { fromLonLat } from 'ol/proj';
import LineString from 'ol/geom/LineString.js';

import Point from 'ol/geom/Point';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import XYZ from 'ol/source/XYZ';
import { Style, Icon, Text, Fill, Stroke } from 'ol/style';
import { mapSource as defaultMapSource } from '../../config';

import * as carMarkers from './car-markers';

/**
 * Принцыпы работы фильтров:
 * 1. Маркер добавляется но отображается только если проходит фильтр.
 *  - При добавлении точек которые должны фильтроваться
 *    в их объекты добавляются поля по которым нужно будет фильтровать.
 *    (В точке авто, например, поле callsign_status)
 *  - При обновлении статуса у маркера автомобиля обновляется соответствующее поле и
 *    получаются новые стили.
 *
 *
 * Принципы работы класса:
 * - Маркеры разного типа лежат в разных слоях.
 * - Публичные методы управляют отображением. Создавая абстракцию над библиотекой.
 * - Все публичные методы требуют имя слоя в котором происходят изменения. Имя слоя можно хранить в кмпонентах.
 * - Класс сам работает с zoom карты и перестраивает маркеры если они зависят от zoom.
 * - 
 * 
 * Функции класса:
 * 1. createMapWithFeatureLayer() создает карту с первым слоем, маркеров.
 * 2. addAllCarsFeatures() добавляет на карту маркеры автомобилей,
 *    сразу сохраняет объект внутри класса, запускается один раз. Во второй раз все перезапишет.
 *    - удалит все маркеры с указанного слоя;
 *    - создаст новые маркеры в указанном слое;
 * 3. updateCarsMarkers() добавляет/удаляет маркеры автомобилей с карты если изменился исходный объект с данными.
 *    сами данные хранятся в переменной mapData класса и перезаписываются новыми.
 * 4. removeFeatureById() Удаляет маркер полностью с карты и объектов хранения.
 * 5. updateFeatureCoordinatesWithGeoPos() Обновляет координаты используя объект который приходит по сокету.
 * 6. setNewStatusToFeature() Устанавливает новый статус для маркера авто.
 * 7. updateCarMarkerFilters() Обновляет фильтр.
 * 8. createNewFeatureLayer() Этот метод используется для создания домолнительного
 *    слоя, например, для маркера поиска или маршрутов.
 * !Методы которые начинаются нижним подчеркиванием "_" - приватные не предполагается что их нужно использовать вне класса.
*/

/**
 * Класс MapView создает, хранит и изменяет состояние карты.
 */
class MapView {
  /**
   * Экземпляр карты, будет хранить объект карты
  */
  mapInstance;

  /* vectorSourcesDataAndFeatures
  * Объект хранит слои, фильтры и маркеры в следующей структруре:
    {
      [sourceName]: {
        filters: [{[fieldName]: Array<number>}], // ???????
        source: sourceObject,
        features: [sourceFeatures],
        visibleFeatures: [sourceVisibleFeatures],
        carsLayer: boolean,
      },
      ...
    }
   * Это необходимо для хранения видимых/не видимых марекеров без необходимости получать
   * в класс каждый раз все данные об объектах и перестраивать всю карту,
   * таким образом будет возможность применять различные фильтры,
   * не прередавая при этом сами даные.
  */
  vectorSourcesAndFeatures = {};

  /**
   * Объект хранения овтомобилей
   * Используется для сравнения объекта в компоненте с объектом карты.
   */
  carsData = [];

  /**
   * Зум карты
   */
  mapZoom = 11;

  /**
   * Метод отображает маршрут карте.
   * @param {Array<string>} polylinesArray Массив полилиний (специальные строки).
   * @param {string} sourceId имя слоя
   * @param {Array<number>} color цвет массив RGBA вида: [28, 124, 84, 1]
   */
  displayRoute = (routeX, routeY, sourceId) => {
    if(routeX.length > 0){
      const list = []
    for (const i in routeX) {
      if (routeX[i] && routeY[i]) {
        list.push([routeX[i], routeY[i]]);
      }
    }

    const listOfPoints = list.map(point => fromLonLat(point));
    const route = new Feature({
      geometry: new LineString(listOfPoints),
    })

    route.setStyle([
      new Style({
        stroke: new Stroke({
          color: '#fff',
          width: 7,
        })
      }),
      new Style({
        stroke: new Stroke({
          color: '#3190a6',
          width: 4,
        })
      })
    ]);
    this.vectorSourcesAndFeatures[sourceId].source.addFeature(route)
    }
    
  };

  /**
   * Метод очищает слой от маркеров
   * @param {string} sourceId id слоя который требуется очистить от маркеров
   * @return {boolean} Проверит что все удалилось и вернет true || false
   */
  clearSourceFromAllMarkers = (sourceId) => {
    this.vectorSourcesAndFeatures[sourceId].source.clear(true);
    this.vectorSourcesAndFeatures[sourceId].features = [];
    this.vectorSourcesAndFeatures[sourceId].visibleFeatures = [];
    const sourceFeatures = this.vectorSourcesAndFeatures[sourceId].source.getFeatures();
    return !sourceFeatures.length
      && !this.vectorSourcesAndFeatures[sourceId].features.length
      && !this.vectorSourcesAndFeatures[sourceId].visibleFeatures.legth;
  }

  /**
   * Метод устанавливает произвольный маркер в указанный слой с указанием иконки.
   * может быть использован для установки маркера поиска или любых других маркеров.
   * @param  {Array<number>} coordinates координаты маркера
   * @param  {string} sourceId слой
   * @param  {string} pointIcon url уконки маркера
   * @param  {string} pointId id иконки который будет так же использован как name
   * @param  {number} scale увеличение/уменьшение иконки
   * @param  {Array<number>} anchor смещение маркера
   * @return {Object} point объект классса ol/geom/Point~Point
   */
  addMarkerToSource = (coordinates, sourceId, pointIcon, pointId, scale = 1, anchor = [0.5, 1]) => {
    const point = new Feature({
      geometry: new Point(fromLonLat(coordinates)),
      name: pointId,
    });
    const style = new Style({
      image: new Icon({
        src: pointIcon,
        scale,
        anchor,
      }),
    });
    point.setStyle(style);
    point.setId(pointId);
    this._addFeature(point, sourceId);
    return point;
  };

  carsFeaturesExist() {
    const layerKeys = Object.keys(this.vectorSourcesAndFeatures);
    let exist = false;
    layerKeys.forEach(key => {
      const layer = this.vectorSourcesAndFeatures[key];
      if (layer.carsLayer) {
        if (layer.features.length) {
          exist = true;
        }
      }
    });
    return exist;
  }

  updateCarsMarkers(carsArray, sourceId) {
    carsArray.forEach(car => {
      const carInCarsData = this.carsData.some(carInCarData => carInCarData.callsign === car.callsign);
      if (!carInCarsData) {
        // Создаем объект стилей для маркера:
        const carMarkerStyles = this._createCarMarkerStyles(car.callsign, car.callsign_status);
        // Создаем точку: объект класса ol/Feature~Feature
        const carPoint = this._createCarPoint(car, carMarkerStyles);
        // Добавляем точку на карту:
        this._addFeature(carPoint, sourceId);
      }
    });
    this.carsData = [...carsArray];
  }

  /**
   * Удаляет полностью указанный feature c карты и объектов хранения:
   * @param {string} featureId Id feature
   * @param {string} sourceId Id слоя с которого нужно удалить
   */
  removeFeatureById(featureId, sourceId) {
    try {
      if (typeof featureId !== 'string' || typeof sourceId !== 'string') throw new TypeError('removeFeatureById() incorrect arguments type!');
      const featureIndex = this.vectorSourcesAndFeatures[sourceId].features.findIndex(feature => feature.id_ === featureId);
      if (featureIndex !== -1) {
        this.vectorSourcesAndFeatures[sourceId].features.splice(featureIndex, 1);
      }
      const visibleFeatureIndex = this.vectorSourcesAndFeatures[sourceId].visibleFeatures.findIndex(feature => feature.id_ === featureId);
      if (visibleFeatureIndex !== -1) {
        this.vectorSourcesAndFeatures[sourceId].visibleFeatures.splice(visibleFeatureIndex, 1);
      }
      const featureToRemove = this.vectorSourcesAndFeatures[sourceId].source.getFeatureById(featureId);
      if (featureToRemove) {
        this.vectorSourcesAndFeatures[sourceId].source.removeFeature(featureToRemove);
      }
    } catch (error) {
      console.error('error', error); // eslint-disable-line no-console
    }
  };

  /**
   * Обновляет координаты авто маркера получая geoPosObject 
   * @param  {Object} geoPosObject
   * @param  {string} sourceId
   */
  updateFeatureCoordinatesWithGeoPos(geoPosObject, sourceId) {
    try {
      const { callsign, coordinates } = formatGeoPosObject(geoPosObject);
      if (!callsign || !coordinates) throw new Error('updateFeatureCoordinatesWithGeoPos() invalid result from formatGeoPosObject()');
      this._updateFeatureCoordinates(`${callsign}`, coordinates, sourceId);
    } catch (error) {
      console.error('error', error); // eslint-disable-line no-console
    }
  }

  /**
   * Метод устанавливает новый статус для маркера
   * @param  {string} featureId id маркера
   * @param  {number} status номер статуса согласно словаря
   * @param  {string} sourceId id слоя
   */
  setNewStatusToFeature(featureId, status, sourceId) {
    try {
      if (typeof featureId !== 'string') throw new TypeError('setNewStatusToFeature() no correct type for featureId');
      if (typeof status !== 'number') throw new TypeError('setNewStatusToFeature() no correct type for status, status');
      if (typeof sourceId !== 'string') throw new TypeError('setNewStatusToFeature() no correct type for sourceId, sourceId');
    } catch (error) {
      console.error('error', error); // eslint-disable-line
    }
    const currentSource = this.vectorSourcesAndFeatures[sourceId];
    const featureToUpdate = currentSource.source.getFeatureById(featureId);
    if (featureToUpdate) {
      featureToUpdate.setProperties({ callsign_status: status }, true);
    }
    // Нужно назнатчить свойства в массивах видимых и не видимых маркеров
    const featureIndex = currentSource.features.findIndex(feature => feature.id_ === featureId);
    if (featureIndex !== -1) {
      currentSource.features[featureIndex].setProperties({ callsign_status: status }, true);
    }
    const visibleFeatureIndex = currentSource.visibleFeatures.findIndex(feature => feature.id_ === featureId);
    if (visibleFeatureIndex !== -1) {
      currentSource.visibleFeatures[visibleFeatureIndex].setProperties({ callsign_status: status }, true);
    }
    this._rebuildSingleCarFeatureStyles(featureId, sourceId);
  };

  /**
   * Метод обновляет фильтр для указанного поля и запускает функцию перестройки карты согласно фильтрам
   * @param  {string} filterField Поле по которому делается фильтрация
   * @param  {Array<number>} filterArray Массив чисел согласно словарю для указанного поля
   * @param  {string} sourceId Имя source для которого применяется фильтр
   */
  updateCarMarkerFilters(filterField, sourceId, filterArray) {
    try {
      if (typeof filterField !== 'string'
        || typeof sourceId !== 'string'
        || typeof filterArray !== 'object') {
        throw new Error('Incorrect arguments types in updateMarkerFilters, arguments');
      }
    } catch (error) {
      console.error(error); // eslint-disable-line no-console
    }

    this._updateFilter({ [filterField]: filterArray }, sourceId);
    this._consistentVisibleMarkersWithFilters();
    this._rebuildMapToConsistentVisible();
  }

  /** Метод создает экземпляр карты с 1 VectorSource и слоем для маркеров
   * @param {String} divId див элемент в котором будет создана карта
   * @param {String} mapSource url для источника даных карты
   * @param {Array} center массив координат
   * @param {number} zoom число, масштаб
   * @param {String} sourceId строка
   * @param {boolean} carsLayer булево значение определяющее является ли слой слоем автомобилей,
   * необходимо для перестойки стилей после зменения zoom
   * @return {Object<string, Map>} Объект карты и sourceId указанный при вызове или сгенерированный sourceId
   * sourceId нужно использовать для доступа к нужному source.
   */
  createMapWithFeatureLayer(divId, mapSource = defaultMapSource, center, zoom, sourceId = this._genSourceId(), carsLayer) {
    try {
      if (typeof divId !== 'string') throw new TypeError('createMapWithFeatureLayer() incorrect type for divId');
      if (typeof mapSource !== 'string') throw new TypeError('createMapWithFeatureLayer() incorrect type for mapSource');
      if (typeof center !== 'object') throw new TypeError('createMapWithFeatureLayer() incorrect type for center');
      if (typeof zoom !== 'number') throw new TypeError('createMapWithFeatureLayer() incorrect type for zoom');
      if (typeof sourceId !== 'string') throw new TypeError('createMapWithFeatureLayer() incorrect type for sourceId');
      if (typeof carsLayer !== 'boolean') throw new TypeError('createMapWithFeatureLayer() incorrect type for carsLayer');
    } catch (error) {
      console.error('error', error) // eslint-disable-line no-console
    }
    const vectorSource = this._createVectorSource();
    const featuresLayer = this._createVectorLayer(vectorSource);
    if (!this.mapInstance) {
      this.mapInstance = this._createMap(mapSource, featuresLayer, center, zoom, divId);
      this._setZoomListener();
    }
    // При создании экземпляра карты сразу создается объект для хранения состояния маркеров
    this.vectorSourcesAndFeatures[sourceId] = {
      source: vectorSource,
      filters: [],
      features: [],
      visibleFeatures: [],
      carsLayer,
    }
    return { sourceId, map: this.mapInstance };
  }

  /**
   * Метод создает новый слой для маркеров на карте
   * @param  {string} sourceId имя слоя
   * @param  {boolean} carsLayer булево значение показывает является ли этот слой слоем автомобилей
   */
  createNewFeatureLayer(sourceId, carsLayer) {
    try {
      if (typeof sourceId !== 'string') throw new TypeError('createNewFeatureLayer() incorrect type for sourceId');
      if (typeof carsLayer !== 'boolean') throw new TypeError('createNewFeatureLayer() incorrect type for carsLayer');
    } catch (error) {
      console.error('error', error); // eslint-disable-line no-console
    }
    if (!this.vectorSourcesAndFeatures[sourceId]) {
      const vSource = this._createVectorSource();
      const featuresLayer = this._createVectorLayer(vSource);
      this.vectorSourcesAndFeatures[sourceId] = {
        source: vSource,
        features: [],
        filters: [],
        visibleFeatures: [],
        carsLayer: carsLayer || false,
      }
      if (this.mapInstance) {
        this.mapInstance.addLayer(featuresLayer);
      }
    }
  }

  /** Метод добавляет на карту все маркеры автомобилей
   * @param  {Object} cars Массив объектов с информацией об автомобилях
   * @param  {string} sourceId Имя source в который будут добавлены маркеры
   * @return {string} возвращает sourceId на который добавились маркеры
   */
  addAllCarsFeatures(cars, sourceId) {
    // Запись данных из которых формирутеся карта что бы потом сравнивать.
    this.carsData = [...cars];
    // Если метод запускается не в первый раз очистим слой от маркеров.
    if (!Array.isArray(cars)) {
      throw new Error('cars variable in addAllCarsFeatures method is not Array');
    }
    if (this.vectorSourcesAndFeatures[sourceId].features.length) {
      this.vectorSourcesAndFeatures[sourceId].source.clear(true);
    }
    const allPoints = []
    cars.forEach(car => {
      // Создаем объект стилей для маркера:
      // if()
      const carMarkerStyles = this._createCarMarkerStyles(car.callsign, car.callsign_status);
      // Создаем точку: объект класса ol/Feature~Feature
      const carPoint = this._createCarPoint(car, carMarkerStyles);
      // Добавляем точку на карту:
      allPoints.push(carPoint);
    });
    this.vectorSourcesAndFeatures[sourceId].source.addFeatures(allPoints);
    this.vectorSourcesAndFeatures[sourceId].features = allPoints;
    this._consistentVisibleMarkersWithFilters();
    this._rebuildMapToConsistentVisible();
    return sourceId;
  }

  /**
   * Метод обновляет позицию маркера.
   * @param  {string} featureId id маркера
   * @param  {Array<number>} coordinates массив с координатам
   * @param  {string} sourceId
   */
  _updateFeatureCoordinates(featureId, coordinates, sourceId) {
    try {
      if (typeof featureId !== 'string') throw new TypeError('updateFeatureCoordinates() not correct type for featureId');
      if (typeof coordinates !== 'object' || coordinates.length !== 2) {
        throw new Error('updateFeatureCoordinates() not correct value or type for coordinates');
      }
      if (typeof sourceId !== 'string') throw new TypeError('updateFeatureCoordinates() not correct type for sourceId');
      const point = new Point(fromLonLat(coordinates));
      const currentSource = this.vectorSourcesAndFeatures[sourceId];
      const featureToUpdate = currentSource.source.getFeatureById(featureId);
      if (featureToUpdate) {
        // Это сделает изменение позиции маркера на карте:
        featureToUpdate.setGeometry(point);
      }
      // Нужно заменить маркеры в массивах видимых и не видимых маркеров
      const featureIndex = currentSource.features.findIndex(feature => feature.id_ === featureId);
      if (featureIndex !== -1) {
        currentSource.features[featureIndex].setGeometry(point);
      }
      const visibleFeatureIndex = currentSource.visibleFeatures.findIndex(feature => feature.id_ === featureId);
      if (visibleFeatureIndex !== -1) {
        currentSource.visibleFeatures[visibleFeatureIndex].setGeometry(point);
      }
    } catch (error) {
      console.error('error', error); // eslint-disable-line
    }
  };

  _createCarMarkerStyles(callsign, callsign_status) {
    return new Style({
      image: new Icon({
        anchor: [0.5, 1],
        src: this._getIconPath(getIconType(this.mapZoom), callsign_status),
        scale: 1,
      }),
      text: getIconType(this.mapZoom) !== 'dot' && new Text({
        text: '',
        offsetY: getTextOffset(getIconType(this.mapZoom)),
        font: `${getMarkerFontSize(getIconType(this.mapZoom))}px sans-serif`,
        stroke: new Stroke({
          color: [255, 255, 255, 1],
        }),
        fill: new Fill({
          color: [255, 255, 255, 1],
        }),
      }),
    });
  }

  /**
   * Метод устанавливает зум,
   * Далее zoom, будет использован для пересчета маркеров, что бы отображать нужный тип маркера
   * @param  {number} value zoom карты
   * @return {bolean} вернет true если зум установлен
   */
  _setMapZoom(value) {
    if (typeof value !== 'number') {
      throw new Error('_setMapZoom() You try to set incorrect value to zoom!');
    }
    this.mapZoom = value;
    this._zoomChangeRebuild();
    return true;
  }


  /**
   * Метод вычисляет url иконки которую нужно отобразить на карте.
   * @param {string} iconType результат который возвращает функция getIconType вычисляющая тип маркера
   * @param {number} callsignStatus числовой код позывного
   * @return {string} src для картинки маркера
   */
  _getIconPath(iconType, callsignStatus) {
    return carMarkers.default[`${iconType}${callsignStatus || '8'}`];
  }

  /**
   * Функция добавляет/обновляет фильтр.
   * Если filter содержит пустой массив то он удаляется из filters слоя
   * Если filter уже есть на слое то обновляется
   * Если filter нет и он не пустой то добавляется
   * @param  {Object<[fieldName]: Array<number>>} filter Объект фильтра
   * @param  {string} sourceId имя source на который добаляется фильтр
   */
  _updateFilter(filterToAdd, sourceId) {
    try {
      if (typeof filterToAdd !== 'object' || typeof sourceId !== 'string') {
        throw new TypeError('updateFilter() incorrect arguments types');
      }
    } catch (error) {
      console.error('error', error); // eslint-disable-line no-console
    }
    const sourceFilters = this.vectorSourcesAndFeatures[sourceId].filters;
    const filterNames = sourceFilters.map(filter => Object.keys(filter)[0]);
    const filterName = Object.keys(filterToAdd)[0];
    const filterInFilters = filterNames.some(filter => filter === filterName);
    // Если фильтр по указанному полю уже есть
    // То в зависимости что пришло или зменяем фильтр на новый или удаляем совсем
    if (filterInFilters) {
      const filterIndex = sourceFilters.findIndex(filter => {
        const filterNameInSource = Object.keys(filter)[0];
        if (filterNameInSource === filterName) {
          return true;
        }
        return false;
      });
      sourceFilters.splice(filterIndex, 1, filterToAdd);
    } else {
      // Если такого фильтра еще нет то необходимо добавить
      sourceFilters.push(filterToAdd);
    }
  }

  /**
   * Функция обновляет состояние visibleFeatures таким
   * образом что бы оно соответствовало фильтрам.
   */
  _consistentVisibleMarkersWithFilters() {
    const sourceIds = Object.keys(this.vectorSourcesAndFeatures);
    // Для каждого фильтра в каждом слое создадим visibleFeatures
    sourceIds.forEach(sourceId => {  
      const currentSource = this.vectorSourcesAndFeatures[sourceId];
      // Тут переписываем visibleFeatures c помощью reduce
      // Так если значение фильтруемого поля есть в фильтре маркер будет видимым.
      if (!currentSource.filters.length) {
        currentSource.visibleFeatures = [...currentSource.features];
        return;
      }

      currentSource.visibleFeatures = currentSource.features.reduce((prev, current) => {
        // Точка должна пройти все фильтры, если фильтр пустой то точка проходит фильтр 
        const passFilters = currentSource.filters.every(filter => {
          // Имя фильтруемого поля в тукущем фильтре
          // сам фильтр это: { fieldname: [filterNumbers] }
          const fieldName = Object.keys(filter)[0];
          // Если фильтр пустой то точка проходит фильтр
          if (filter[fieldName].length === 0) {
            return true;
          }
          return filter[fieldName].some(filterNumber => current.values_[fieldName] === filterNumber);
        });
        if (passFilters) {
          prev.push(current);
        }
        return prev;
      }, []);
    });
  }

  /**
   * Функция перестраивает карту таким образом что бы были отображены только маркеры
   * из visibleFeatures текущего слоя
   */
  _rebuildMapToConsistentVisible() {
    const sourceIds = Object.keys(this.vectorSourcesAndFeatures);
    sourceIds.forEach(sourceId => {
      const currentLayer = this.vectorSourcesAndFeatures[sourceId];
      if (!currentLayer.visibleFeatures.length) {
        currentLayer.source.clear();
      }
      // Проход по массиву видимых маркеров что бы отобразить те которые не отображены.
      currentLayer.visibleFeatures.forEach(feature => {
        const sourceHasFeature = currentLayer.source.hasFeature(feature);
        // Если такого маркера нет на отображенной карте, то добавляем.
        if (!sourceHasFeature) {
          currentLayer.source.addFeature(feature);
        }
      })
      // Проход в обратную сторону для удаления маркеров которых нет в visibleFeature
      currentLayer.source.forEachFeature(feature => {
        const featureInVisible = currentLayer.visibleFeatures.some(featureInVisibleArr => featureInVisibleArr.id_ === feature.id_);
        // Если маркера нет в visible то удаляем его с карты
        if (!featureInVisible) {
          currentLayer.source.removeFeature(feature);
        }
      })
    });
  }

  _setZoomListener() {
    this.mapInstance.on('moveend', () => {
      const currentZoom = this.mapInstance.getView().getZoom();
      this._setMapZoom(currentZoom);
    });
  }

  _genSourceId() {
    return `source-${Math.round(Math.random() * 1000)}`;
  }

  /**
   * @param  {string} mapSource Url источника данных карты (тайлов)
   * @param  {Object} featuresLayer Слой маркеров
   * @param  {Array<number>} center Массив координат [30.5035, 50.4548]
   * @param  {number} mapZoom Число параметр масштаба (11)
   * @param  {string} divId Id дива в котором будет лежать карта
   * @return {Object<Map>} объект карты
   */
  _createMap(mapSource, featuresLayer, center, mapZoom, divId) {
    try {
      if (typeof mapSource !== 'string') throw new TypeError('_createMap() incorrect type for mapSource');
      if (typeof featuresLayer !== 'object') throw new TypeError('_createMap() incorrect type for featureLayer');
      if (typeof center !== 'object') throw new TypeError('_createMap() incorrect type for center');
      if (typeof mapZoom !== 'number') throw new TypeError('_createMap() incorrect type for mapZoom');
      if (typeof divId !== 'string') throw new TypeError('_createMap() incorrect type for divId');
    } catch (error) {
      console.error('error', error); // eslint-disable-line no-console
    }
    const newMap = new Map({
      layers: [
        this._createTileLayer(mapSource),
        featuresLayer,
      ],
      view: new View({
        center: fromLonLat(center),
        zoom: mapZoom,
      }),
      target: divId,
    });
    return newMap;
  }

  /** Создает слой тайлов
   * @param  {string} source Url источника данных карты (тайлов)
   * @return {Object} ol/layer/Tile~TileLayer
   */
  _createTileLayer(source) {
    const tileLayer = new TileLayer({
      source: new XYZ({
        url: `${source}/tile/{z}/{x}/{y}.png`,
      }),
    });
    return tileLayer;
  }

  /** Создает объект класса ol/source/Vector~VectorSource
   * @param  {Array} features Не обязательный парметр, массив маркеров
   */
  _createVectorSource(features = []) {
    const vector = new VectorSource({
      features,
    });
    return vector;
  }

  /** Создает векторный слой
   * @param {vectorSource}
   * @return {Object} объект класса ol/layer/Vector~VectorLayer
   */
  _createVectorLayer(vectorSource) {
    const layer = new VectorLayer({
      source: vectorSource,
    });
    return layer;
  };

  /**
   * Методод меняет стили всех маркеров после изменения zoom карты
   */
  _zoomChangeRebuild() {
    const layersKeys = Object.keys(this.vectorSourcesAndFeatures);
    layersKeys.forEach((key, index) => {
      const layer = this.vectorSourcesAndFeatures[key];
      const sourceId = Object.keys(this.vectorSourcesAndFeatures)[index];
      // Если это слой авто-маркеров перестраиваем
      if (layer.carsLayer) {
        layer.source.forEachFeature(feature => {
          const newStyles = this._createCarMarkerStyles(feature.id_, feature.values_.callsign_status);
          feature.setStyle(newStyles);
          // Вносим изменения в массивы хранения
          this.vectorSourcesAndFeatures[sourceId].features.forEach(featureInFeatures => {
            if (feature.id_ === featureInFeatures.id_) {
              featureInFeatures.setStyle(newStyles);
            }
          });
          this.vectorSourcesAndFeatures[sourceId].visibleFeatures.forEach(featureInVisibleFeatures => {
            if (feature.id_ === featureInVisibleFeatures.id_) {
              featureInVisibleFeatures.setStyle(newStyles);
            }
          });
        })
      }
    })
  }

  /**
   * Создает точку для маркера автомобиля
   * @param {Object} carObj 
   * @param {Object} style не обязательный параметр,
   * если не передан стиль для точки будет создан с помощью метода _createCarMarkerStyles
   * @return {Object} Возвращает объъект класса ol/geom/Point~Point
   */
  _createCarPoint(carObj, style) {
    const { car } = carObj;

    const coordinatesExist = (carObject) => carObject.gps && carObject.gps.coordinates && carObject.gps.coordinates.length === 2;

    const point = new Feature({
      geometry: coordinatesExist(carObj) ? new Point(fromLonLat(carObj.gps.coordinates)) : null,
      name: `${carObj.callsign}-${car.license_plate}`,
      callsign: `${carObj.callsign}`,
      // Поле нужно для фильтрации
      callsign_status: carObj.callsign_status,
      transporter: carObj.transporter ? carObj.transporter.id : null,
    });
    if (style) {
      point.setStyle(style);
    } else {
      point.setStyle(this._createCarMarkerStyles(carObj.callsign, carObj.callsign_status, carObj.gps_available));
    }
    point.setId(carObj.callsign);
    return point;
  }

  /** Добавляет точку на указанный source
   * Если такая точка уже есть то не добавляет в массив точек если нет то добавлет.
   * @param  {Object} point Объект класса ol/geom/Point~Point
   * @param  {String} sourceId sourceId на который следует добавить точку
   */
  _addFeature(point, sourceId) {
    try {
      const pointExist = this.vectorSourcesAndFeatures[sourceId].features.some(feature => feature.id_ === point.id_);
      if (!pointExist) {
        this.vectorSourcesAndFeatures[sourceId].features.push(point);
        // функция _consistentVisibleMarkersWithFilters добавит
        // в видимые маркеры если нет фильтра
        // для даного типа точек
      }
      this._consistentVisibleMarkersWithFilters();
      this._rebuildMapToConsistentVisible();
    } catch (error) {
      console.error('error', error); // eslint-disable-line no-console
    }
  }

  /**
   * Перестраивает стили маркеров автомобилей
   * @param  {string} featureId
   * @param  {string} sourceId
   */
  _rebuildSingleCarFeatureStyles(featureId, sourceId) {
    const feature = this.vectorSourcesAndFeatures[sourceId].source.getFeatureById(featureId);
    try {
      if (typeof featureId !== 'string') throw new TypeError('_rebuildSingleFeatureStyles() incorrect feature type');
      if (typeof sourceId !== 'string') throw new TypeError('_rebuildSingleFeatureStyles() incorrect sourceId type');
      if (!feature) { // TODO раскомметировать в релизе пока мешает сыпет много ошибок
        // throw new Error('_rebuildSingleFeatureStyles() feature not found!');
      }
    } catch (error) {
      console.error('error', error) // eslint-disable-line no-console
    }
    // Выход из функции если feature не найден
    // TODO маркер не найден - такого не должно быть
    // Пока сервер может слать обновления не системно, т.е. не связано с основным объектом данных.
    if (!feature) {
      return;
    }
    const newFeatureStyles = this._createCarMarkerStyles(feature.id_, feature.values_.callsign_status);
    this.vectorSourcesAndFeatures[sourceId].source.forEachFeature(featureInSource => {
      if (featureInSource.id_ === feature.id_) {
        featureInSource.setStyle(newFeatureStyles);
      }
    })
    this.vectorSourcesAndFeatures[sourceId].visibleFeatures.forEach(featureInVisible => {
      if (feature.id_ === featureInVisible.id_) {
        featureInVisible.setStyle(newFeatureStyles);
      }
    });
    this.vectorSourcesAndFeatures[sourceId].features.forEach(featureInFeatures => {
      if (feature.id_ === featureInFeatures.id_) {
        featureInFeatures.setStyle(newFeatureStyles)
      }
    })
  }
}

export default MapView;

/**
 * Функция делает следующее:
 * 1. сортирует timestamps по убыванию, таким образом по индексу 0 последняя дата,
 * 2. потом выбираем координаты для этой даты и складываем в массив
 * @param  {string} payloadString // представленный строкой объект приходит как payload в topic,в ней предск callsign, набор координат и еще несколько не нужных.
 * @return {Object<{callsign: number, coordinates: Array<string>}>} // Возвращаем объект с 2 полями
 */
export function formatGeoPosObject(payloadObject) {
  const datesArray = Object.keys(payloadObject.gps_data);
  datesArray.sort((date1, date2) => new Date(date2) - new Date(date1));
  const minDate = datesArray[0];
  const coordinates = [
    payloadObject.gps_data[minDate].lng,
    payloadObject.gps_data[minDate].lat,
  ];
  const { callsign } = payloadObject;

  return { callsign, coordinates };
}

/**
 * Функция принимает тип иконки, вычисляет смещение по оси Y для попапа
 * @param  {string} iconType // Зум карты
 * @return {number} // смещение по оси Y для попапа
 */
export function getYPopupOffset(iconType) {
  switch (iconType) {
    case 'large':
      return -85;
    case 'big':
      return -66;
    case 'normal':
      return -50;
    case 'dot':
      return -40;
    default:
      return -50;
  }
}

/**
 * @param  {string} iconType результат который возвращает функция getIconType вычисляющая тип маркера
 * @return {number} размер шрифта для маркера автомобиля
 */
export function getMarkerFontSize(iconType) {
  switch (iconType) {
    case 'large':
      return 22;
    case 'big':
      return 18;
    case 'normal':
      return 16;
    default:
      return 16;
  }
}

/**
 * @param  {number} zoom
 * @return {'large'|'big'|'normal'|'dot'} возвращет строку с типом иконки
 * расчитывая ее исходя из параметра zoom
 */
export function getIconType(zoom) {
  if (zoom > 17) {
    return 'dot';
  }
  if (zoom >= 15 && zoom < 17) {
    return 'dot';
  }
  if (zoom > 12 && zoom < 15) {
    return 'dot';
  }
  if (zoom <= 12) {
    return 'dot';
  }
  return 'dot';
}

/**
 * standart: #ffc10e
 * business: #56c4cf
 * premium: #009574
 * busy: #ff547c
 * any: #797c7e
 */

/**
 * Возвращет цвет, если в параметрах что то не ожиданное
 * вернет #333 - серый
 * @param  {1|2|3} carClass // число может быть 1 или 2 или 3
 * @return {string} // цвет соответствующий car_class или серый.
 */
export function getColorFromCarClass(carClass) {
  switch (carClass) {
    case 1:
      // standart
      return '#ffc10e';
    case 2:
      // business
      return '#56c4cf';
    case 3:
      // premium
      return '#009574';
    default:
      return '#333';
  }
}

export function getTextOffset(iconType) {
  switch (iconType) {
    case 'large':
      return -45;
    case 'big':
      return -35;
    case 'normal':
      return -22;
    default:
      return 0;
  }
}
